import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Typography,
  Popper,
  List,
  ListSubheader,
  Grid,
  Divider,
  Theme,
  Checkbox,
  ListItemText,
  ListItemIcon,
  ToggleButton,
  ListItemButton,
  Box,
  ClickAwayListener,
} from '@mui/material';
import { Tooltip } from '@globalvisioninc/ui/tooltip';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  getFocusedDifference,
  inspection,
  getIsOCR,
  getIsSingleFile,
  getSelectedModule,
  getDifferenceGroups,
  getDifferenceStatusFilters,
  getHighlightColor,
} from 'store';
import {
  Difference,
  CTFModuleTypes,
  DifferenceTypeNames,
  FormattingDifferences,
  ContentDifferences,
  getDifferenceDisplayName,
  SpellingDifferences,
  CheckDifferences,
} from 'types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import { getStyleVariables } from 'styles/vars';
import { useTracker } from '../../Tracker/TrackerProvider';
import { makeStyles } from 'tss-react/mui';
import { filterTypeMappings, useFilters } from 'pdftron/docManager/hooks/useFilters';
import { differenceHighlightColors, DifferenceTypesWithHighlightColor } from 'pdftron/docManager/differenceColors';
import { ColorId } from '@verify/shared-utils';
import DifferenceStatusFilters from '../ResultPage/DifferenceStatusFilters';
import {
  selectPrepDifferenceGroups,
  selectPrepDifferenceStatusFilters,
  selectPrepFocusedDifference,
  selectPrepHighlightColor,
  selectPrepIsOCR,
  selectPrepIsSingleFile,
  selectPrepSelectedModule,
} from './filterListPrepSelectors';
import styles from './FilterList.module.css';

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    // Container styles
    popperContainer: {
      position: 'relative' as const,
      zIndex: 2,
    },
    filteredList: {
      zIndex: 2,
    },
    // Shared dropdown container styles
    dropdownBase: {
      backgroundColor: 'var(--gds-color-cool-gray-700)',
      borderRadius: '4px',
      boxShadow:
        'rgb(0 0 0 / 20%) 0px 3px 3px -2px, rgb(0 0 0 / 14%) 0px 3px 4px 0px, rgb(0 0 0 / 12%) 0px 1px 8px 0px',
    },
    filterListContainer: {
      overflowY: 'auto' as const,
      maxHeight: '54vh',
    },
    prepFilteredList: {
      overflow: 'hidden',
      height: '54vh',
      maxHeight: '320px',
    },
    singleFileFilteredList: {
      height: 'auto',
    },

    // Header styles
    subheader: {
      padding: theme.spacing(2),
      paddingBottom: '10px',
    },
    subheaderTitle: {
      textTransform: 'uppercase' as const,
      fontSize: '10px',
      fontWeight: 600,
      color: 'var(--gds-color-text-supportive)',
      letterSpacing: 1.25,
    },

    // List styles
    list: {
      width: 300,
      height: '100%',
      display: 'flex',
      flexDirection: 'column' as const,
      border: `solid 1px ${styleVariables.global.darkBorder}`,
      borderRadius: '5px',
    },
    listItem: {
      paddingTop: 0,
      paddingBottom: 0,
    },

    // Filter row styles
    filters: {
      overflowY: 'auto' as const,
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    filtersNoPadding: {
      overflowY: 'auto' as const,
      paddingTop: 0,
      paddingBottom: theme.spacing(1),
    },
    scrollBar: styleVariables.scrollBar,
    filterItem: {
      paddingRight: theme.spacing(0.8),
    },
    filterSubType: {
      marginLeft: theme.spacing(3),
    },
    mainType: {
      height: '30px',
    },
    subType: {
      height: '26px',
    },

    // Checkbox styles
    checkbox: {
      '&.Mui-disabled': {
        opacity: 0.38,
        color: theme.palette.text.secondary,
        '& .MuiIconButton-label:after': {
          display: 'none',
        },
      },
    },
    leftAlignedCheckbox: {
      padding: theme.spacing(0.25, 0.5),
      marginLeft: theme.spacing(-0.5),
    },
    rightAlignedCheckbox: {
      padding: theme.spacing(0.25, 0.5),
    },
    listItemWidth: {
      minWidth: '42px',
      justifyContent: 'flex-end',
    },

    // Text styles
    displayContents: {
      '&>.MuiTypography-body2': {
        fontSize: '11px',
      },
    },
    largeDisplayContents: {
      '&>.MuiTypography-body2': {
        fontSize: '12px',
        fontWeight: '700',
      },
    },

    // Divider
    seperator: {
      backgroundColor: theme.palette.action.disabled,
    },

    // Icon styles
    icon: {
      width: theme.spacing(2),
      height: theme.spacing(2),
      cursor: 'pointer',
    },
    zoneInfoIcon: {
      marginLeft: 'auto',
      margin: theme.spacing(0.25, 1),
      fontSize: '14px',
      color: '#fff',
    },

    // Toggle button styles
    toggleButton: {
      borderRadius: '50%',
      minWidth: 0,
      padding: theme.spacing(0.5),
      margin: theme.spacing(0.25),
      color: 'rgba(255,255,255,.7)',
      border: 'none',
      '&:hover': {
        color: 'rgba(255,255,255)',
      },
    },
    modified: {
      color: '#fff',
      backgroundColor: theme.palette.secondary.main,
      borderRadius: '50%',
      '&:hover': {
        backgroundColor: theme.palette.secondary.main,
        color: 'rgba(255,255,255)',
      },
      '&.Mui-selected': {
        backgroundColor: theme.palette.secondary.main,
        color: '#fff',
        '&:hover': {
          backgroundColor: theme.palette.secondary.main,
        },
      },
    },
    disabled: {
      color: 'rgba(255,255,255,.37) !important',
    },
    colorSquare: {
      width: '10px',
      height: '10px',
      borderRadius: '2px',
      flexShrink: 0,
    },
  };
});

export type FilterListVariant = 'prep' | 'results';

export interface FilterListProps {
  variant: FilterListVariant;
  disabled?: boolean;
}

/** Maps single-type module tabs to their corresponding filter type. */
const MODULE_FILTER_TYPE: Partial<Record<CTFModuleTypes, DifferenceTypeNames>> = {
  [CTFModuleTypes.graphics]: DifferenceTypeNames.Graphics,
  [CTFModuleTypes.barcode]: DifferenceTypeNames.Barcode,
  [CTFModuleTypes.braille]: DifferenceTypeNames.Braille,
  [CTFModuleTypes.color]: DifferenceTypeNames.Color,
  [CTFModuleTypes.fontMetrics]: DifferenceTypeNames.FontMetrics,
  [CTFModuleTypes.markup]: DifferenceTypeNames.Markup,
};

const FilterList = ({ variant, disabled = false }: FilterListProps) => {
  const isPrep = variant === 'prep';
  const isResults = variant === 'results';

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const tracker = useTracker();

  // These selectors vary between Prep and Results, so we use the appropriate selector for each variant.
  const differenceGroups: Difference[] = useSelector(isResults ? getDifferenceGroups : selectPrepDifferenceGroups);
  const focusedDifference = useSelector(isResults ? getFocusedDifference : selectPrepFocusedDifference);
  const isSingleFile = useSelector(isResults ? getIsSingleFile : selectPrepIsSingleFile);
  const isOCR = useSelector(isResults ? getIsOCR : selectPrepIsOCR);
  const selectedModule = useSelector(isResults ? getSelectedModule : selectPrepSelectedModule);
  const differenceStatusFilters = useSelector(
    isResults ? getDifferenceStatusFilters : selectPrepDifferenceStatusFilters,
  );
  const highlightColor = useSelector(isResults ? getHighlightColor : selectPrepHighlightColor);
  const eitherFileIsOCR = isResults && (isOCR.sourceIsOCR || isOCR.targetIsOCR);
  const anchorEl = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<Element | null>(null);
  const [open, setOpen] = useState(false);

  // For Prep variant, find the panel container
  useEffect(() => {
    if (isPrep) {
      panelRef.current = document.querySelector('.GVInspectionPanel');
      if (!panelRef.current) {
        panelRef.current = document.querySelector('.settings-content');
      }
    }
  }, [isPrep]);

  // Handle click outside to close dropdown
  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;
    if (dropdownRef.current?.contains(target)) {
      return;
    }
    setOpen(false);
  }, []);

  useEffect(() => {
    // Only use manual click outside handling for Results (Prep uses ClickAwayListener)
    if (open && isResults) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [open, isResults, handleClickOutside]);

  const { typeFilters, textFilters, toggleFilter, updateFilters } = useFilters();

  // Use textFilters for Prep (simpler), typeFilters for Results (full)
  const filters = isPrep ? textFilters : typeFilters;

  const diffCounts: { [index: string]: number } = {};

  // For Results, calculate counts from difference groups
  if (isResults) {
    const defaultFilters: DifferenceTypeNames[] = isSingleFile
      ? [
          DifferenceTypeNames.SpellingGeneral,
          DifferenceTypeNames.Abbreviation,
          DifferenceTypeNames.ProperNoun,
          DifferenceTypeNames.Alphanumeric,
          DifferenceTypeNames.Barcode,
          DifferenceTypeNames.Braille,
        ]
      : [
          DifferenceTypeNames.Insertion,
          DifferenceTypeNames.Deletion,
          DifferenceTypeNames.Change,
          DifferenceTypeNames.Case,
          DifferenceTypeNames.Space,
          DifferenceTypeNames.ReadingOrder,
          DifferenceTypeNames.Bold,
          DifferenceTypeNames.Italic,
          DifferenceTypeNames.Underlined,
          DifferenceTypeNames.Subscript,
          DifferenceTypeNames.FontType,
          DifferenceTypeNames.FontSize,
          DifferenceTypeNames.FontColor,
          DifferenceTypeNames.SoftHyphen,
          DifferenceTypeNames.LineBreakSpace,
          DifferenceTypeNames.SpellingGeneral,
          DifferenceTypeNames.Abbreviation,
          DifferenceTypeNames.ProperNoun,
          DifferenceTypeNames.Alphanumeric,
          DifferenceTypeNames.Barcode,
          DifferenceTypeNames.Graphics,
          DifferenceTypeNames.Braille,
          DifferenceTypeNames.EmbeddedImage,
          DifferenceTypeNames.UnmatchedAnnotation,
          DifferenceTypeNames.Markup,
          DifferenceTypeNames.Color,
        ];

    defaultFilters.forEach((diffType) => (diffCounts[diffType] = 0));

    differenceGroups.forEach((diff) => {
      const type = filterTypeMappings[diff.type] || diff.type;
      if (!diffCounts[type]) {
        diffCounts[type] = 0;
      }
      diffCounts[type]++;
    });
  }

  const trackChange = (key: string, checked: boolean) => {
    if (isResults) {
      tracker.track({
        name: 'Difference-filter',
        data: {
          differenceType: key,
          checked,
        },
      });
    }
  };

  const handleClick = (type: string) => {
    if (isResults) {
      if (
        typeFilters[type] === true &&
        focusedDifference?.type &&
        focusedDifference?.displayName === getDifferenceDisplayName(type as DifferenceTypeNames)
      ) {
        dispatch(inspection.actions.unfocusDifference());
      }
      trackChange(type, typeFilters[type]);
    }
    toggleFilter(type as any);
  };

  // Compute filter states
  const isEveryFilterEnabled = isResults
    ? Object.keys(diffCounts).every(
        (type) =>
          diffCounts[type] === 0 ||
          typeFilters[type] === false ||
          (selectedModule === CTFModuleTypes.text && !(type in ContentDifferences || type in FormattingDifferences)) ||
          (selectedModule === CTFModuleTypes.spelling && !(type in SpellingDifferences)) ||
          (selectedModule === CTFModuleTypes.fontMetrics && !(type in CheckDifferences)),
      )
    : Object.values(filters).every((isFiltered) => !isFiltered);

  const isStatusModified = isResults
    ? Object.keys(differenceStatusFilters)
        .filter((key) => key !== 'smartDiscard')
        .some((key) => differenceStatusFilters[key] === true)
    : false;

  const isContentModified = isResults
    ? Object.keys(diffCounts).some((type) => type in ContentDifferences && typeFilters[type] === true)
    : Object.keys(filters).some((type) => type in ContentDifferences && filters[type] === true);

  const isFormattingModified = isResults
    ? Object.keys(diffCounts).some((type) => type in FormattingDifferences && typeFilters[type] === true)
    : Object.keys(filters).some((type) => type in FormattingDifferences && filters[type] === true);

  const isSpellingModified = isResults
    ? Object.keys(diffCounts).some((type) => type in SpellingDifferences && typeFilters[type] === true)
    : false;

  const hasContentDiffs = isResults
    ? Object.keys(diffCounts).some((type) => type in ContentDifferences && diffCounts[type] > 0)
    : true;
  const hasFormattingDiffs = isResults
    ? Object.keys(diffCounts).some((type) => type in FormattingDifferences && diffCounts[type] > 0)
    : true;
  const hasSpellingDiffs = isResults
    ? Object.keys(diffCounts).some((type) => type in SpellingDifferences && diffCounts[type] > 0)
    : false;

  const isContentEnabled = isResults
    ? hasContentDiffs &&
      Object.keys(diffCounts).some(
        (type) => type in ContentDifferences && diffCounts[type] > 0 && typeFilters[type] === false,
      )
    : Object.keys(filters).some((type) => type in ContentDifferences && filters[type] === false);

  const isFormattingEnabled = isResults
    ? hasFormattingDiffs &&
      Object.keys(diffCounts).some(
        (type) => type in FormattingDifferences && diffCounts[type] > 0 && typeFilters[type] === false,
      )
    : Object.keys(filters).some((type) => type in FormattingDifferences && filters[type] === false);

  const isSpellingEnabled = isResults
    ? hasSpellingDiffs &&
      Object.keys(diffCounts).some(
        (type) => type in SpellingDifferences && diffCounts[type] > 0 && typeFilters[type] === false,
      )
    : false;

  const toggleContent = () => {
    if (isResults) {
      const contentDiffs = Object.keys(diffCounts).filter((type) => type in ContentDifferences && diffCounts[type] > 0);
      updateFilters(contentDiffs as any, isContentEnabled);
    } else {
      updateFilters(Object.keys(filters).filter((type) => type in ContentDifferences) as any, isContentEnabled);
    }
  };

  const toggleFormatting = () => {
    if (isResults) {
      const formattingDiffs = Object.keys(diffCounts).filter(
        (type) => type in FormattingDifferences && diffCounts[type] > 0,
      );
      updateFilters(formattingDiffs as any, isFormattingEnabled);
    } else {
      updateFilters(Object.keys(filters).filter((type) => type in FormattingDifferences) as any, isFormattingEnabled);
    }
  };

  const toggleSpelling = () => {
    if (isResults) {
      const spellingDiffs = Object.keys(diffCounts).filter(
        (type) => type in SpellingDifferences && diffCounts[type] > 0,
      );
      updateFilters(spellingDiffs as any, isSpellingEnabled);
    }
  };

  const toggleSelectAll = () => {
    if (isResults) {
      let types = Object.keys(diffCounts).filter((type) => diffCounts[type] > 0);
      if (selectedModule === CTFModuleTypes.text) {
        types = types.filter((type) => type in FormattingDifferences || type in ContentDifferences);
      } else if (selectedModule === CTFModuleTypes.spelling) {
        types = types.filter((type) => type in SpellingDifferences);
      }
      types.forEach((type) => {
        trackChange(getDifferenceDisplayName(type as DifferenceTypeNames), isEveryFilterEnabled);
      });
      updateFilters(types as DifferenceTypeNames[], isEveryFilterEnabled);
    } else {
      updateFilters(Object.keys(filters) as any[], isEveryFilterEnabled);
    }
  };

  const renderFilterRow = (type: string, large = false, isSubType = false) => {
    const shouldShowColorSquare =
      isResults &&
      highlightColor === ColorId.MULTICOLOR &&
      DifferenceTypesWithHighlightColor.includes(type as any) &&
      differenceHighlightColors[type];

    const count = diffCounts[type] ?? 0;
    const isChecked = isResults ? count > 0 && typeFilters[type] === false : !filters[type as any];

    const isDisabled = isResults ? count === 0 : false;

    const filterLabel = isResults
      ? `${getDifferenceDisplayName(type as any)} (${count})`
      : getDifferenceDisplayName(type as DifferenceTypeNames);

    return (
      <ListItemButton
        className={`${isSubType ? classes.subType : classes.mainType} ${classes.filterItem} ${classes.listItem}`}
        key={`filterCheckbox_${type}`}
        id={isPrep ? `prepFilterCheckBox${type}` : `filterCheckbox_${type}`}
        onClick={() => handleClick(type)}
        disabled={isDisabled}
      >
        <Grid container alignItems="center">
          {!isSubType && (
            <Checkbox
              className={`${classes.checkbox} ${classes.leftAlignedCheckbox}`}
              color="secondary"
              checked={isChecked}
              disabled={isDisabled}
            />
          )}
          <ListItemText
            className={`${large ? classes.largeDisplayContents : classes.displayContents} ${
              isSubType && (isPrep || selectedModule !== CTFModuleTypes.spelling) ? classes.filterSubType : ''
            }`}
            primary={
              shouldShowColorSquare ? (
                <Box display="flex" alignItems="center">
                  <span>{filterLabel}</span>
                  <Box
                    className={classes.colorSquare}
                    sx={{
                      backgroundColor: differenceHighlightColors[type],
                      marginLeft: 1,
                    }}
                  />
                </Box>
              ) : (
                filterLabel
              )
            }
          />
        </Grid>
        <ListItemIcon className={classes.listItemWidth}>
          {isSubType && (
            <>
              {isResults && (Object.values(FormattingDifferences) as string[]).includes(type) && eitherFileIsOCR ? (
                <GVTooltip
                  title="This difference type is unavailable while inspecting an OCR'd document."
                  className={styles.tooltip}
                >
                  <InfoOutlinedIcon className={classes.zoneInfoIcon} style={{ pointerEvents: 'auto' }} />
                </GVTooltip>
              ) : (
                <Checkbox
                  className={`${classes.checkbox} ${classes.rightAlignedCheckbox}`}
                  color="secondary"
                  checked={isChecked}
                  disabled={isDisabled}
                />
              )}
            </>
          )}
        </ListItemIcon>
      </ListItemButton>
    );
  };

  const renderFilters = () => {
    const textContentFilters: string[] = [];
    const textFormattingFilters: string[] = [];
    const spellingFilters: string[] = [];
    const otherFilters: string[] = [];

    if (isResults) {
      Object.keys(diffCounts)
        .sort((a, b) => (diffCounts[b] === 0 && diffCounts[a] !== 0 ? -1 : 1))
        .forEach((filter) => {
          if (filter in ContentDifferences) {
            textContentFilters.push(filter);
          } else if (filter in FormattingDifferences) {
            textFormattingFilters.push(filter);
          } else if (filter in SpellingDifferences) {
            spellingFilters.push(filter);
          } else if (filter !== DifferenceTypeNames.Markup || diffCounts[DifferenceTypeNames.Markup] > 0) {
            otherFilters.push(filter);
          }
        });
    } else {
      Object.keys(filters).forEach((filter) => {
        if (ContentDifferences[filter as keyof typeof ContentDifferences]) {
          textContentFilters.push(filter);
        } else if (FormattingDifferences[filter as keyof typeof FormattingDifferences]) {
          textFormattingFilters.push(filter);
        }
      });
    }

    const sortedOtherFilters = [...otherFilters];
    const sortOrder: any = { [DifferenceTypeNames.FontMetrics]: -1 };
    sortedOtherFilters.sort((a, b) => {
      const aOrder = sortOrder[a] ?? 0;
      const bOrder = sortOrder[b] ?? 0;
      return bOrder - aOrder;
    });

    return (
      <>
        {(isPrep || selectedModule === 'All' || selectedModule === CTFModuleTypes.text) && (
          <>
            <ListItemButton
              className={`${classes.mainType} ${classes.filterItem} ${classes.listItem}`}
              disabled={!hasContentDiffs}
              onClick={toggleContent}
            >
              <Checkbox
                className={`${classes.checkbox} ${classes.leftAlignedCheckbox}`}
                color="secondary"
                checked={isContentEnabled && !isContentModified}
                indeterminate={isContentEnabled && isContentModified}
              />
              <ListItemText className={classes.largeDisplayContents} primary="Text Content" />
            </ListItemButton>
            {textContentFilters.map((filter) => renderFilterRow(filter, false, true))}
            <ListItemButton
              className={`${classes.mainType} ${classes.filterItem} ${classes.listItem}`}
              disabled={!hasFormattingDiffs}
              onClick={toggleFormatting}
            >
              <Checkbox
                className={`${classes.checkbox} ${classes.leftAlignedCheckbox}`}
                color="secondary"
                checked={isFormattingEnabled && !isFormattingModified}
                indeterminate={isFormattingEnabled && isFormattingModified}
              />
              <ListItemText className={classes.largeDisplayContents} primary="Text Formatting" />
            </ListItemButton>
            {textFormattingFilters.map((filter) => renderFilterRow(filter, false, true))}
          </>
        )}
        {isResults && (selectedModule === 'All' || selectedModule === CTFModuleTypes.spelling) && (
          <>
            <ListItemButton
              className={`${classes.mainType} ${classes.filterItem} ${classes.listItem}`}
              disabled={!hasSpellingDiffs}
              onClick={toggleSpelling}
            >
              {selectedModule !== CTFModuleTypes.spelling && (
                <Checkbox
                  className={`${classes.checkbox} ${classes.leftAlignedCheckbox}`}
                  color="secondary"
                  checked={isSpellingEnabled && !isSpellingModified}
                  indeterminate={isSpellingEnabled && isSpellingModified}
                />
              )}
              <ListItemText className={classes.largeDisplayContents} primary="Spelling" />
            </ListItemButton>
            {spellingFilters.map((filter) => renderFilterRow(filter, false, true))}
          </>
        )}
        {isResults && selectedModule === 'All'
          ? sortedOtherFilters.map((filter) => renderFilterRow(filter, true))
          : null}
      </>
    );
  };

  const handleToggle = (event: React.MouseEvent) => {
    if (isResults) {
      (event as any).stopPropagation?.();
    }
    setOpen(!open);
  };

  const handleClose = (event: any) => {
    if (anchorEl.current?.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const popperClassName = isPrep
    ? `${classes.dropdownBase} ${classes.prepFilteredList}`
    : `${classes.filteredList} ${isSingleFile ? classes.singleFileFilteredList : ''}`;

  const buttonModified = isResults ? !isEveryFilterEnabled || isStatusModified : !isEveryFilterEnabled;

  const showTextFilters =
    isPrep ||
    selectedModule === 'All' ||
    selectedModule === CTFModuleTypes.spelling ||
    selectedModule === CTFModuleTypes.text;

  const moduleFilterType = isResults ? MODULE_FILTER_TYPE[selectedModule as CTFModuleTypes] : undefined;

  const renderSelectAll = () => (
    <>
      <ListItemButton onClick={toggleSelectAll} id="filterCheckbox" className={classes.listItem}>
        <ListItemText primary="Select All" className={classes.displayContents} />
        <ListItemIcon className={classes.listItemWidth}>
          <Checkbox
            color="secondary"
            className={`${classes.checkbox} ${classes.rightAlignedCheckbox}`}
            checked={isEveryFilterEnabled}
          />
        </ListItemIcon>
      </ListItemButton>
      <Divider component="li" className={classes.seperator} />
    </>
  );

  const renderTextFilters = () => (
    <Grid className={`${classes.filters} ${isPrep ? classes.scrollBar : ''}`}>{renderFilters()}</Grid>
  );

  // Combined render for Prep (Select All + filters together)
  const renderSelectAllAndFilters = () => (
    <>
      {renderSelectAll()}
      {renderTextFilters()}
    </>
  );

  const renderModuleFilter = () => {
    if (!moduleFilterType) return null;
    return <Grid className={classes.filters}>{renderFilterRow(moduleFilterType, true)}</Grid>;
  };

  const renderDropdownContent = () => {
    const listContent = (
      <List
        className={classes.list}
        dense
        style={{ paddingBottom: 0 }}
        subheader={
          <ListSubheader disableSticky disableGutters className={classes.subheader}>
            <Grid container>
              <Grid item xs={8}>
                <Typography variant="body1" className={classes.subheaderTitle}>
                  {isPrep ? 'Text Filters' : 'Filters'}
                </Typography>
              </Grid>
            </Grid>
          </ListSubheader>
        }
      >
        {isResults && <DifferenceStatusFilters isPopperOpen={open} />}
        {isResults ? (
          <>
            {showTextFilters && renderSelectAll()}
            {showTextFilters && renderTextFilters()}
            {!showTextFilters && renderModuleFilter()}
          </>
        ) : (
          renderSelectAllAndFilters()
        )}
      </List>
    );

    if (isPrep) {
      return <ClickAwayListener onClickAway={handleClose}>{listContent}</ClickAwayListener>;
    }
    // Results dropdown
    return (
      <Box ref={dropdownRef} className={`${classes.dropdownBase} ${classes.filterListContainer} ${classes.scrollBar}`}>
        {listContent}
      </Box>
    );
  };

  return (
    <Box className={isPrep ? classes.popperContainer : undefined}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <ToggleButton
            value="filteredButton"
            data-testid={isPrep ? 'prepTextFiltersButton' : 'FilterResultsButton'}
            selected={open}
            className={`${classes.toggleButton} ${buttonModified ? classes.modified : ''}`}
            onMouseDown={isResults ? (e) => e.stopPropagation() : undefined}
            onClick={handleToggle}
            disabled={disabled}
            classes={{ disabled: classes.disabled }}
            disableRipple
            ref={anchorEl}
          >
            <FilterListIcon className={classes.icon} />
          </ToggleButton>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content side="bottom">Filter</Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
      {!disabled && (
        <Popper
          open={open}
          anchorEl={anchorEl.current}
          placement="bottom-end"
          className={popperClassName}
          id="filteredList"
          modifiers={[
            { name: 'flip', enabled: false },
            { name: 'preventOverflow', enabled: false },
            { name: 'hide', enabled: false },
            { name: 'offset', options: { offset: [0, 8] } },
          ]}
          style={isPrep ? { zIndex: 2 } : undefined}
          container={isPrep ? panelRef.current : undefined}
          disablePortal={isPrep}
          popperOptions={isPrep ? { strategy: 'absolute' } : undefined}
        >
          {renderDropdownContent()}
        </Popper>
      )}
    </Box>
  );
};

export default FilterList;
